import { formatMoney } from 'common/src/other/currencyFormat';
import React, { useState, useEffect } from "react";
import { Typography, Grid, Avatar, Box, Rating, Button } from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment/min/moment-with-locales";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import { GoogleMap, Marker, Polyline } from "@react-google-maps/api";
import { api } from "common";
import { getLangKey } from "common/src/other/getLangKey";
import { C2_FONT } from "../components/conecta2ModalStyles";

const NO_TRACKING_STATUSES = ["NEW", "PAYMENT_PENDING"];

const CARD_SX = {
  backgroundColor: "#FBFAFE",
  borderRadius: "16px",
  border: "1px solid #D7CFEB",
  boxShadow: "0 8px 24px rgba(26,15,59,0.06)",
  overflow: "hidden",
  height: "100%",
  display: "flex",
  flexDirection: "column",
};

const statusStyle = (status) => {
  if (status === "CANCELLED") {
    return { bg: "#EF4444", color: "#FBFAFE" };
  }
  if (status === "COMPLETE" || status === "PAID") {
    return { bg: "#16A34A", color: "#FBFAFE" };
  }
  if (status === "STARTED" || status === "ACCEPTED" || status === "ARRIVED") {
    return { bg: "#6411ED", color: "#FBFAFE" };
  }
  return { bg: "#EBE7FB", color: "#1A0F3B" };
};

function SectionCard({ title, icon: Icon, children }) {
  return (
    <Box sx={CARD_SX}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2,
          py: 1.75,
          background: "linear-gradient(90deg, #6411ED 0%, #7628FF 100%)",
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            backgroundColor: "rgba(251,250,254,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ color: "#FBFAFE", fontSize: 20 }} />
        </Box>
        <Typography
          sx={{
            fontFamily: C2_FONT,
            fontWeight: 800,
            fontSize: "0.95rem",
            color: "#FBFAFE",
            letterSpacing: "0.02em",
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 2, flex: 1 }}>{children}</Box>
    </Box>
  );
}

function DetailRow({ label, value, isRTL, valueSx }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "flex-start" },
        justifyContent: "space-between",
        gap: 0.75,
        py: 1.15,
        borderBottom: "1px solid #EDE8F7",
        direction: isRTL === "rtl" ? "rtl" : "ltr",
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Typography
        sx={{
          fontFamily: C2_FONT,
          fontSize: "0.82rem",
          fontWeight: 600,
          color: "#766293",
          flex: "0 0 42%",
          textAlign: isRTL === "rtl" ? "right" : "left",
        }}
      >
        {label}
      </Typography>
      <Typography
        component="div"
        sx={{
          fontFamily: C2_FONT,
          fontSize: "0.9rem",
          fontWeight: 600,
          color: "#1A0F3B",
          flex: 1,
          textAlign: isRTL === "rtl" ? "left" : "right",
          wordBreak: "break-word",
          ...valueSx,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function TripRouteCard({ bookingId, t, isRTL }) {
  const [state, setState] = useState({ loading: true, points: [], summary: null, error: null });
  const [map, setMap] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, points: [], summary: null, error: null });
    api.fetchTripTracking(bookingId).then((res) => {
      if (cancelled) return;
      setState({
        loading: false,
        points: res.success ? (res.points || []).map((p) => ({ lat: p.lat, lng: p.lng })) : [],
        summary: res.success ? res.summary : null,
        error: res.success ? null : res.error,
      });
    });
    return () => { cancelled = true; };
  }, [bookingId]);

  useEffect(() => {
    if (!map || state.points.length < 2) return;
    const bounds = new window.google.maps.LatLngBounds();
    state.points.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 40);
  }, [map, state.points]);

  const { loading, points, summary, error } = state;
  const message = loading
    ? t("loading", { defaultValue: "Cargando recorrido..." })
    : error || (!points.length ? t("no_tracking_available", { defaultValue: "Este viaje no tiene recorrido GPS guardado." }) : null);

  return (
    <SectionCard title={t("trip_route", { defaultValue: "Recorrido GPS" })} icon={RouteOutlinedIcon}>
      {message ? (
        <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.9rem", color: "#766293" }}>{message}</Typography>
      ) : (
        <>
          <Box sx={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #EDE8F7" }}>
            <GoogleMap
              mapContainerStyle={{ height: "360px", width: "100%" }}
              center={points[0]}
              zoom={14}
              onLoad={setMap}
              onUnmount={() => setMap(null)}
              options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}
            >
              <Polyline path={points} options={{ strokeColor: "#6411ED", strokeOpacity: 0.9, strokeWeight: 4 }} />
              <Marker position={points[0]} label="A" />
              {points.length > 1 ? <Marker position={points[points.length - 1]} label="B" /> : null}
            </GoogleMap>
          </Box>
          {summary ? (
            <Box sx={{ mt: 1 }}>
              <DetailRow label={t("distance_web")} value={`${summary.distanceKm} ${t("km")}`} isRTL={isRTL} />
              <DetailRow label={t("total_time")} value={`${Math.round(summary.durationMin)} min`} isRTL={isRTL} />
              <DetailRow label={t("gps_points", { defaultValue: "Puntos GPS" })} value={summary.points} isRTL={isRTL} />
            </Box>
          ) : null}
        </>
      )}
    </SectionCard>
  );
}

function BookingDetails() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const settings = useSelector((state) => state.settingsdata.settings);
  const role = useSelector((state) => state.auth.profile.usertype);
  const { state } = useLocation();

  useEffect(() => {
    if (bookinglistdata.bookings) {
      const booking = bookinglistdata.bookings.filter(
        (item) => item.id === id.toString()
      )[0];

      if (booking) {
        setData(booking);
      } else {
        navigate("/404");
        setData(null);
      }
    }
  }, [bookinglistdata.bookings, id, navigate]);

  const st = statusStyle(data?.status);

  const rideRows = data
    ? [
        { key: "booking_id", label: t("booking_id"), value: data.id },
        { key: "booking_ref", label: t("booking_ref"), value: data.reference },
        {
          key: "booking_status_web",
          label: t("booking_status_web"),
          value: (
            <Box
              component="span"
              sx={{
                display: "inline-block",
                px: 1.25,
                py: 0.4,
                borderRadius: "999px",
                backgroundColor: st.bg,
                color: st.color,
                fontFamily: C2_FONT,
                fontWeight: 700,
                fontSize: "0.78rem",
                textTransform: "uppercase",
              }}
            >
              {t(data.status)}
            </Box>
          ),
        },
        ...(data.paymentStatus === "unpaid_reported"
          ? [{
              key: "payment_status",
              label: "Pago",
              value: (
                <Box component="span" sx={{ fontFamily: C2_FONT, fontWeight: 700, color: "#B91C1C" }}>
                  Reportada sin pago
                </Box>
              ),
            }]
          : []),
        {
          key: "trip_start_date",
          label: t("trip_start_date"),
          value: data?.tripdate ? moment(data?.tripdate).format("lll") : "",
        },
        { key: "trip_start_time", label: t("trip_start_time"), value: data?.trip_start_time },
        { key: "trip_end_time", label: t("trip_end_time"), value: data?.trip_end_time },
        {
          key: "parcel_type_web",
          label: t("parcel_type_web"),
          value: data.parcelTypeSelected
            ? t(getLangKey(data.parcelTypeSelected.description)) +
              " (" +
              data.parcelTypeSelected.amount +
              ")"
            : "",
        },
        {
          key: "parcel_option",
          label: t("parcel_option"),
          value: data.optionSelected
            ? t(getLangKey(data.optionSelected.description)) +
              " (" +
              data.optionSelected.amount +
              ")"
            : "",
        },
        { key: "deliveryPerson", label: t("deliveryPerson"), value: data?.deliveryPerson },
        {
          key: "deliveryPersonPhone",
          label: t("deliveryPersonPhone"),
          value: data.deliveryPersonPhone
            ? settings.AllowCriticalEditsAdmin
              ? data.deliveryPersonPhone
              : t("hidden_demo")
            : "",
        },
        {
          key: "take_pickup_image_web",
          label: t("take_pickup_image_web"),
          value: data.pickup_image ? (
            <Box
              component="img"
              src={data.pickup_image}
              alt="pickup_image"
              sx={{ width: 120, height: 90, borderRadius: "10px", objectFit: "cover" }}
            />
          ) : "",
        },
        {
          key: "take_deliver_image_web",
          label: t("take_deliver_image_web"),
          value: data.deliver_image ? (
            <Box
              component="img"
              src={data.deliver_image}
              alt="deliver_image"
              sx={{ width: 120, height: 90, borderRadius: "10px", objectFit: "cover" }}
            />
          ) : "",
        },
        { key: "tripInstructions", label: t("tripInstructions"), value: data?.tripInstructions },
        {
          key: "roundTrip",
          label: t("roundTrip"),
          value: data?.roundTrip === true || data?.roundTrip === 1 || data?.roundTrip === "1"
            ? t("yes")
            : data?.roundTrip === false || data?.roundTrip === 0
              ? t("no")
              : "",
        },
        { key: "cancellation_reason", label: t("cancellation_reason"), value: data?.reason ? t(getLangKey(data.reason)) : "" },
        { key: "otp", label: t("otp"), value: data?.otp ? t("true") : t("false") },
        { key: "total_time", label: t("total_time"), value: data?.total_trip_time },
        {
          key: "distance_web",
          label: t("distance_web"),
          value: data.distance
            ? settings.convert_to_mile
              ? data?.distance + t("mile")
              : data?.distance + " " + t("km")
            : "",
        },
        { key: "pickUpInstructions_web", label: t("pickUpInstructions_web"), value: data?.pickUpInstructions },
        { key: "deliveryInstructions", label: t("deliveryInstructions"), value: data?.deliveryInstructions },
        { key: "otherPerson", label: t("otherPerson"), value: data?.otherPerson },
        { key: "otherPersonPhone", label: t("otherPersonPhone"), value: data?.otherPersonPhone },
        { key: "feedback", label: t("feedback"), value: data?.feedback },
        {
          key: "you_rated_text",
          label: t("you_rated_text"),
          value: data?.rating != null ? (
            <Rating name="half-rating-read" value={data?.rating || 0} precision={0.5} readOnly />
          ) : "",
        },
        {
          key: "pickup_address",
          label: t("pickup_address"),
          value: data?.pickupAddress,
          valueSx: { color: "#16A34A" },
        },
        {
          key: "drop_address",
          label: t("drop_address"),
          value: data?.dropAddress,
          valueSx: { color: "#EF4444" },
        },
      ]
    : [];

  const paymentRows = data
    ? [
        {
          key: "trip_cost",
          label: t("trip_cost"),
          value:
            role === "customer" && ["NEW", "PAYMENT_PENDING"].indexOf(data?.status) !== -1
              ? t("bid").toUpperCase()
              : formatMoney(data?.trip_cost || 0, settings, { isRTL: !!settings.swipe_symbol }),
        },
        {
          key: "Customer_paid",
          label: t("Customer_paid"),
          value:
            data?.paymentStatus === "unpaid_reported"
              ? "Reportada sin pago"
              : data?.customer_paid != null && data?.customer_paid !== ""
                ? formatMoney(data.customer_paid, settings, { isRTL: !!settings.swipe_symbol })
                : "",
        },
        {
          key: "discount_ammount",
          label: t("discount_ammount"),
          value: formatMoney(data?.discount || 0, settings, { isRTL: !!settings.swipe_symbol }),
        },
        {
          key: "driver_share",
          label: t("driver_share"),
          value:
            role === "customer" && ["NEW", "PAYMENT_PENDING"].indexOf(data?.status) !== -1
              ? "--"
              : formatMoney(data?.driver_share || 0, settings, { isRTL: !!settings.swipe_symbol }),
        },
        {
          key: "fleet_admin_comission",
          label: t("fleet_admin_comission"),
          value:
            role === "customer" && ["NEW", "PAYMENT_PENDING"].indexOf(data?.status) !== -1
              ? "--"
              : data?.fleetCommission && parseFloat(data?.fleetCommission) > 0
                ? formatMoney(data.fleetCommission, settings, { isRTL: !!settings.swipe_symbol })
                : "",
        },
        {
          key: "convenience_fee",
          label: t("convenience_fee"),
          value:
            role === "customer" && ["NEW", "PAYMENT_PENDING"].indexOf(data?.status) !== -1
              ? "--"
              : formatMoney(data?.convenience_fees || 0, settings, { isRTL: !!settings.swipe_symbol }),
        },
        {
          key: "tipamount",
          label: t("tipamount"),
          value: data?.tipamount
            ? formatMoney(data.tipamount, settings, { isRTL: !!settings.swipe_symbol })
            : "",
        },
        {
          key: "cancellationFee",
          label: t("cancellationFee"),
          value: formatMoney(data?.cancellationFee || 0, settings, { isRTL: !!settings.swipe_symbol }),
        },
        { key: "payment_gateway", label: t("payment_gateway"), value: data?.gateway },
        { key: "payment_mode_web", label: t("payment_mode_web"), value: data?.payment_mode ? data.payment_mode : "" },
        {
          key: "cash_payment_amount",
          label: t("cash_payment_amount"),
          value: data?.cashPaymentAmount
            ? formatMoney(data.cashPaymentAmount, settings, { isRTL: !!settings.swipe_symbol })
            : "",
        },
        {
          key: "card_payment_amount",
          label: t("card_payment_amount"),
          value: data?.cardPaymentAmount
            ? formatMoney(data.cardPaymentAmount, settings, { isRTL: !!settings.swipe_symbol })
            : "",
        },
        {
          key: "wallet_payment_amount",
          label: t("wallet_payment_amount"),
          value: data?.usedWalletMoney
            ? formatMoney(data.usedWalletMoney, settings, { isRTL: !!settings.swipe_symbol })
            : "",
        },
        {
          key: "payable_ammount",
          label: t("payable_ammount"),
          value: data?.payableAmount
            ? formatMoney(data.payableAmount, settings, { isRTL: !!settings.swipe_symbol })
            : "",
        },
      ]
    : [];

  const driverRows = data
    ? [
        { key: "driver_id", label: t("driver_id"), value: data?.driver ? data.driver : "" },
        { key: "driver_name", label: t("driver_name"), value: data?.driver_name ? data.driver_name : "" },
        {
          key: "driver_contact",
          label: t("driver_contact"),
          value: data?.driver_contact
            ? settings.AllowCriticalEditsAdmin
              ? data?.driver_contact
              : t("hidden_demo")
            : "",
        },
        {
          key: "driver_email",
          label: t("driver_email"),
          value: data?.driver_email
            ? settings.AllowCriticalEditsAdmin
              ? data?.driver_email
              : t("hidden_demo")
            : "",
        },
        { key: "car_type", label: t("car_type"), value: data?.carType ? t(getLangKey(data.carType)) : "" },
        { key: "vehicle_no", label: t("vehicle_no"), value: data?.vehicle_number ? data.vehicle_number : "" },
        { key: "fleetadmins", label: t("fleetadmins"), value: data?.fleetadmin && role === "admin" ? data.fleetadmin : "" },
        { key: "device_id", label: t("device_id"), value: data?.driverDeviceId ? data.driverDeviceId : "" },
      ]
    : [];

  const customerRows = data
    ? [
        { key: "customer_id", label: t("customer_id"), value: data?.customer ? data.customer : "" },
        { key: "customer_name", label: t("customer_name"), value: data?.customer_name ? data.customer_name : "" },
        {
          key: "customer_contact",
          label: t("customer_contact"),
          value: data?.customer_contact
            ? settings.AllowCriticalEditsAdmin
              ? data?.customer_contact
              : t("hidden_demo")
            : "",
        },
        {
          key: "customer_email",
          label: t("customer_email"),
          value: data?.customer_email
            ? settings.AllowCriticalEditsAdmin
              ? data?.customer_email
              : t("hidden_demo")
            : "",
        },
      ]
    : [];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 2.5,
          direction: isRTL === "rtl" ? "rtl" : "ltr",
        }}
      >
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/bookings", { state: { pageNo: state?.pageNo } })}
            sx={{
              fontFamily: C2_FONT,
              fontWeight: 700,
              textTransform: "none",
              color: "#6411ED",
              px: 0,
              mb: 0.5,
              "&:hover": { backgroundColor: "transparent", color: "#5B0FD9" },
            }}
          >
            {t("go_back", { defaultValue: "Volver" })}
          </Button>
          <Typography
            sx={{
              fontFamily: C2_FONT,
              fontWeight: 800,
              fontSize: { xs: "1.35rem", sm: "1.6rem" },
              color: "#1A0F3B",
              letterSpacing: "0.02em",
              textTransform: "uppercase",
            }}
          >
            {t("booking_details", { defaultValue: "Detalle de reserva" })}
          </Typography>
          {data?.reference ? (
            <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.9rem", color: "#766293", mt: 0.4 }}>
              {t("booking_ref", { defaultValue: "Referencia" })}: {data.reference}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <SectionCard title={t("ride_information")} icon={DirectionsCarOutlinedIcon}>
            {rideRows.map((item) => (
              <DetailRow
                key={item.key}
                label={item.label}
                value={item.value}
                isRTL={isRTL}
                valueSx={item.valueSx}
              />
            ))}
          </SectionCard>
        </Grid>

        <Grid item xs={12} sm={12} md={6} lg={3}>
          <SectionCard title={t("payment_info")} icon={PaymentsOutlinedIcon}>
            {paymentRows.map((item) => (
              <DetailRow key={item.key} label={item.label} value={item.value} isRTL={isRTL} />
            ))}
          </SectionCard>
        </Grid>

        <Grid item xs={12} sm={12} md={6} lg={3}>
          <SectionCard title={t("driver_info")} icon={BadgeOutlinedIcon}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              {data?.driver_image ? (
                <Avatar
                  alt="driver profile image"
                  src={data?.driver_image}
                  sx={{
                    width: 88,
                    height: 88,
                    border: "3px solid #EBE7FB",
                    boxShadow: "0 6px 16px rgba(100,17,237,0.2)",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 88,
                    height: 88,
                    borderRadius: "50%",
                    backgroundColor: "#EBE7FB",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AccountCircleIcon sx={{ width: 72, height: 72, color: "#6411ED" }} />
                </Box>
              )}
            </Box>
            {driverRows.map((item) => (
              <DetailRow key={item.key} label={item.label} value={item.value} isRTL={isRTL} />
            ))}
          </SectionCard>
        </Grid>

        <Grid item xs={12} sm={12} md={6} lg={3}>
          <SectionCard title={t("customer_info")} icon={PersonOutlineIcon}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              {data?.customer_image ? (
                <Avatar
                  alt="customer profile image"
                  src={data?.customer_image}
                  sx={{
                    width: 88,
                    height: 88,
                    border: "3px solid #EBE7FB",
                    boxShadow: "0 6px 16px rgba(100,17,237,0.2)",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 88,
                    height: 88,
                    borderRadius: "50%",
                    backgroundColor: "#EBE7FB",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AccountCircleIcon sx={{ width: 72, height: 72, color: "#6411ED" }} />
                </Box>
              )}
            </Box>
            {customerRows.map((item) => (
              <DetailRow key={item.key} label={item.label} value={item.value} isRTL={isRTL} />
            ))}
          </SectionCard>
        </Grid>

        {data?.id && NO_TRACKING_STATUSES.indexOf(data.status) === -1 ? (
          <Grid item xs={12}>
            <TripRouteCard bookingId={data.id} t={t} isRTL={isRTL} />
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
}

export default BookingDetails;
